/**
 * Widget Session Client
 *
 * POST /session — obtain a new session token
 * POST /session/refresh — refresh an existing token (called at 80% TTL)
 */

export interface SessionResponse {
  accessToken: string;
  tokenType: string;
  expiresAt: string;
  sessionId: string;
  canPreview?: boolean;
}

/**
 * Request a new session token from the widget session endpoint.
 * When a previewToken is provided, exchanges it via /session/preview instead.
 * Returns null on 403/404 (domain/geo/policy rejection) — caller should exit silently.
 * Throws on 5xx (caller should render launcher with retry UI).
 */
export async function requestSession(
  apiBase: string,
  orgId: string,
  widgetId: string,
  previewToken?: string,
): Promise<SessionResponse | null> {
  const isPreview = Boolean(previewToken);
  const endpoint = isPreview
    ? `${apiBase}/o/${orgId}/w/${widgetId}/session/preview`
    : `${apiBase}/o/${orgId}/w/${widgetId}/session`;

  const body = isPreview
    ? { previewToken }
    : { origin: window.location.origin, referer: document.referrer };

  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "omit",
  });

  if (response.status === 403 || response.status === 404) {
    return null;
  }

  if (response.status === 429) {
    throw Object.assign(new Error("Session rate limited"), { status: 429 });
  }

  if (!response.ok) {
    throw new Error(`Session request failed: ${response.status}`);
  }

  return response.json() as Promise<SessionResponse>;
}

/**
 * Refresh an existing session token.
 * Only call when >80% of TTL has elapsed (enforced by the server).
 */
export async function refreshSession(
  apiBase: string,
  orgId: string,
  widgetId: string,
  currentToken: string,
): Promise<SessionResponse | null> {
  let response: Response;
  try {
    response = await fetch(`${apiBase}/o/${orgId}/w/${widgetId}/session/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken}`,
      },
      credentials: "omit",
    });
  } catch {
    return null;
  }

  if (!response.ok) {
    return null;
  }

  return response.json() as Promise<SessionResponse>;
}
